import { ApiConfig } from '../api';

/**
 * Custom error class that includes HTTP status code information
 */
export class HttpError extends Error {
  public status: number;
  public response: Response;
  public responseJSON?: any;

  constructor(message: string, status: number, response: Response, responseJSON?: any) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.response = response;
    this.responseJSON = responseJSON;
  }
}

/**
 * Converts an object to URL search parameters string
 *
 * @param obj - Object containing key-value pairs to convert to URL parameters
 * @returns A URL-encoded string of the parameters (without the leading '?')
 *
 * @example
 * // Returns "foo=bar&baz=123"
 * objectToSearchParams({ foo: "bar", baz: 123 })
 *
 * // Skips null and undefined values
 * // Returns "foo=bar"
 * objectToSearchParams({ foo: "bar", baz: null, qux: undefined })
 */
export function objectToSearchParams(obj: Record<string, any>): string {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  return params.toString();
}

/**
 * Performs a fetch request with standardized error handling and JSON content type
 *
 * @template T - The expected return type of the API response
 * @param url - The URL to fetch from
 * @param options - Request options to pass to fetch
 * @returns A promise that resolves to the parsed JSON response of type T
 *
 * @throws HttpError if the response is not ok - includes status code and response object
 *
 * @example
 * // Get user data
 * const userData = await fetchWrapper<User>(`${getEndpoint()}users/123`, {
 *   method: 'GET'
 * });
 *
 * @example
 * // Post data with error handling
 * try {
 *   const result = await fetchWrapper<{ success: boolean }>(`${getEndpoint()}data`, {
 *     method: 'POST',
 *     body: JSON.stringify({ name: 'Test' })
 *   });
 * } catch (error) {
 *   if (error.status === 401) {
 *     // Handle unauthorized
 *   } else if (error.status === 409) {
 *     // Handle conflict
 *   }
 * }
 */
export async function fetchWrapper<T = any>(url: string, options: RequestInit, config: ApiConfig): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    ...config.headers,
    ...options.headers,
  };

  const filteredHeaders = Object.entries(headers).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key as string] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  const response = await fetch(url, {
    ...options,
    headers: filteredHeaders,
  });

  if (!response.ok) {
    let responseJSON;
    try {
      responseJSON = await response.json();
    } catch {
      // If response body can't be parsed as JSON, leave it undefined
      responseJSON = undefined;
    }
    throw new HttpError(`Something went wrong: ${response.status}`, response.status, response, responseJSON);
  }

  // Parse response as JSON only if the response Content-Type is application/json
  let data;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (config.returnHeaders) {
    // HACK: This is a hack to return the headers in the response
    // The types will be correctly set at the top level of the ApiTypeWithHeaders type
    return { responseData: data, headers: response.headers } as T;
  }
  return data;
}
